import React, { useState, useEffect } from 'react';
import {
  Dialog as MuiModal,
  DialogContent as MuiModalContent,
  DialogTitle as MuiDialogTitle,
  makeStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';

import Box from '../Box';
import { SvgIcon } from '../Icon';

const ModalCard = styled(Box)`
  background: white;
`;

const CloseIcon = styled(Box)`
  position: absolute;
  z-index: 1;
  top: 20px;
  right: 20px;
  cursor: pointer;
  &:hover {
    opacity: 0.4;
  }
  &:active {
    opacity: 0.9;
  }
`;

const useStyle = makeStyles({
  modalRoot: {
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  modalPaper: (modalStyle) => ({
    width: '100%',
    minWidth: '300px',
    maxWidth: modalStyle.maxWidth ?? '100%',
    margin: '16px',
    borderRadius: modalStyle.borderRadius,
    height: modalStyle.height,
  }),
  contentRoot: (modalStyle) => ({
    padding: modalStyle.padding ? modalStyle.padding : '24px',
    '&:first-child': {
      paddingTop: modalStyle.padding ? modalStyle.padding : '24px', // Override MUI Styling
    },
  }),
});

const useDialogTitleStyle = makeStyles({
  root: ({ colors }) => ({
    borderBottom: `1px solid ${colors.coolGrey.grey2}`,
  }),
});

const Modal = ({
  isOpen,
  toggleComponent,
  closeIcon,
  children,
  innerProps,
  isControlled,
  modalStyle,
  onOpenModal,
  onCloseModal,
  modalContentProps,
  modalTitle,
  modalTitleProps,
  styleModalContentProps,
  ...muiDialogProps
}) => {
  const [open, setOpen] = useState(isOpen);
  const { colors } = useTheme();

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const handleOpen = () => {
    if (typeof onOpenModal === 'function') onOpenModal();
    !isControlled && setOpen(true);
  };
  const handleClose = () => {
    if (typeof onCloseModal === 'function') onCloseModal();
    !isControlled && setOpen(false);
  };
  const classes = useStyle(modalStyle);
  const dialogTitleClasses = useDialogTitleStyle({ colors });

  return (
    <>
      {React.isValidElement(toggleComponent) &&
        React.cloneElement(toggleComponent, { onClick: handleOpen })}
      <MuiModal
        open={open}
        onClose={handleClose}
        classes={{ root: classes.modalRoot, paper: classes.modalPaper }}
        {...muiDialogProps}
      >
        {closeIcon && (
          <CloseIcon onClick={handleClose} id="close-icon">
            <SvgIcon icon={SvgIcon.ICON_LIST.CLOSE} size={24} />
          </CloseIcon>
        )}
        {modalTitle && (
          <MuiDialogTitle classes={dialogTitleClasses} {...modalTitleProps}>
            {modalTitle}
          </MuiDialogTitle>
        )}
        <MuiModalContent
          classes={{ root: classes.contentRoot }}
          style={{ ...styleModalContentProps }}
          {...modalContentProps}
        >
          <ModalCard {...innerProps}>
            {typeof children === 'function' ? children(handleOpen, handleClose) : children}
          </ModalCard>
        </MuiModalContent>
      </MuiModal>
    </>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  isControlled: PropTypes.bool,
  toggleComponent: PropTypes.node,
  closeIcon: PropTypes.bool,
  onOpenModal: PropTypes.func,
  onCloseModal: PropTypes.func,
  modalContentProps: PropTypes.object,
  modalTitle: PropTypes.element,
  modalTitleProps: PropTypes.object,
  styleModalContentProps: PropTypes.object,
};

Modal.defaultProps = {
  isOpen: false,
  closeIcon: true,
  isControlled: false,
};
export default Modal;
